// Optional Base44 hex-colony showcase. No changes to normal new-game placement.
// The scenario is planned atomically on a cloned grid, then installed on request.
import { useGame } from "../em2c-store";
import { EM2C_BUILDING_LIST, EM2C_BUILDINGS } from "../data/em2c-buildings";
import { TECHS } from "../data/research";
import { axialToWorld, hexDistance, rotateFootprint } from "../hexgrid";
import { validatePlacement, occupyHexes } from "../hexplacement";
import { isFootprintInCommandCoverage, isHexInCommandCoverage } from "../systems/CommandNetworkIntegration";
import { getBuildingRecipesForType } from "../systems/UnifiedSimulation";

export const SHOWCASE_BACKUP_PREFIX = "mars_base44_before_showcase:";
const SAVE_KEYS = [
  "mars_em2c_save_v1", "mars_em2c_autosave_v1",
  "mars_em2c_systems_v2", "mars_em2c_systems_autosave_v2",
];
const REGIONS = {
  core: [0, 0], habitation: [-5, 1], medical: [-7, 3],
  life: [-3, 7], power: [3, -7], mining: [7, 2],
  manufacturing: [8, -3], storage: [0, 6], drones: [-4, -4],
  spaceport: [-8, -2], research: [2, 4], gas: [8, -7],
};
const INFRASTRUCTURE = [
  ["relay_tower", -5, 2], ["relay_tower", 0, 5],
  ["relay_tower", 5, -4], ["relay_tower", -4, -5],
  ["relay_tower", 7, 2],
];
const SUPPORT = [
  ...Array(3).fill("solar_station"),
  ...Array(2).fill("reactor_complex"),
  ...Array(2).fill("battery_storage"),
  ...Array(2).fill("oxygen_gen"),
  ...Array(2).fill("water_purifier"),
  ...Array(2).fill("greenhouse"),
  ...Array(2).fill("habitat_module"),
  ...Array(2).fill("habitat_complex"),
  ...Array(1).fill("water_extractor"),
];
const isDepositBuilding = (type) => Boolean(EM2C_BUILDINGS[type]?.requiresDeposit);
const hexKey = (q, r) => `${q},${r}`;

function freshSurvey(state, grid) {
  const deposits = (state.deposits || []).map((d) => ({
    ...d, remaining: Number(d.capacity || d.remaining || 0),
    discovered: true, hexes: (d.hexes || []).map((h) => ({ ...h })),
  }));
  for (const hex of grid.values()) {
    hex.occupied = false;
    hex.buildingId = null;
    hex.hasDeposit = null;
    hex.depositId = null;
  }
  for (const dep of deposits) {
    for (const cell of dep.hexes?.length ? dep.hexes : [{ q: dep.q, r: dep.r }]) {
      const h = grid.get(hexKey(cell.q, cell.r));
      if (h) { h.hasDeposit = dep.kind; h.depositId = dep.id; }
    }
  }
  return deposits;
}

function candidateOrder(grid, type) {
  const [targetQ, targetR] = REGIONS[EM2C_BUILDINGS[type]?.category] || [0, 0];
  const requiresDeposit = isDepositBuilding(type);
  return [...grid.values()].sort((a, b) => {
    const penalty = (h) => (!requiresDeposit && h.hasDeposit ? 1000 : 0);
    const score = (h) => penalty(h)
      + 5 * hexDistance(h.q, h.r, targetQ, targetR)
      + 0.05 * hexDistance(h.q, h.r, 0, 0);
    return score(a) - score(b) || a.q - b.q || a.r - b.r;
  });
}

function makeInstance(type, q, r, index) {
  const { x, z } = axialToWorld(q, r);
  return {
    id: `showcase-${type}-${index}`, type, q, r, x, z, rotation: 0,
    condition: 1, active: true, progress: 1, workers: 0,
    level: 1, upgradeLevel: 1, state: "operational", recipeProgress: 0,
    constructionStartedSol: 1,
  };
}

export function planShowcaseColony(state) {
  if (!state.grid?.size) throw new Error("Hex map not initialized");
  const grid = new Map([...state.grid].map(([k, h]) => [k, { ...h }]));
  const deposits = freshSurvey(state, grid);
  const buildings = [];
  const selectedRecipes = {};
  const instances = new Map();

  function add(type, preferred = null) {
    const def = EM2C_BUILDINGS[type];
    if (!def) throw new Error(`Missing active building type: ${type}`);
    const possible = preferred
      ? [grid.get(hexKey(preferred[0], preferred[1])), ...candidateOrder(grid, type)]
      : candidateOrder(grid, type);
    for (const hex of possible) {
      if (!hex) continue;
      if (validatePlacement(grid, def, hex.q, hex.r, 0).valid !== true) continue;
      const stateForNetwork = { ...state, grid, buildings };
      if (type !== "command_center" && type !== "mega_command") {
        if (type === "relay_tower") {
          if (!isHexInCommandCoverage(stateForNetwork, hex.q, hex.r)) continue;
        } else if (!isFootprintInCommandCoverage(stateForNetwork, type, hex.q, hex.r, 0)) {
          continue;
        }
      }
      const instance = makeInstance(type, hex.q, hex.r, (instances.get(type) || 0) + 1);
      instance.buildNumber = buildings.length + 1;
      occupyHexes(grid, def.footprintHexes, hex.q, hex.r, 0, instance.id);
      buildings.push(instance);
      instances.set(type, (instances.get(type) || 0) + 1);
      const recipes = getBuildingRecipesForType(type);
      if (recipes.length) selectedRecipes[instance.id] = recipes[0].id;
      return instance;
    }
    throw new Error(`No valid connected hex footprint for ${def.name}`);
  }

  // Roots first; the Command Complex occupies the exact canonical 7-hex cluster.
  add("command_center", [0, 0]);
  add("mega_command", [8, -8]);

  // Network must exist BEFORE remote production and services are placed.
  for (const [type, q, r] of INFRASTRUCTURE) add(type, [q, r]);

  // Give water/mineral production first choice of actual surveyed deposits.
  const remaining = EM2C_BUILDING_LIST
    .map((def) => def.id)
    .filter((type) => !["command_center", "mega_command"].includes(type))
    .filter((type) => type !== "relay_tower")
    .sort((a, b) => Number(isDepositBuilding(b)) - Number(isDepositBuilding(a)));
  for (const type of remaining) add(type);
  for (const type of SUPPORT) add(type);

  const types = new Set(buildings.map((b) => b.type));
  const missing = EM2C_BUILDING_LIST.filter((def) => !types.has(def.id)).map((def) => def.id);
  if (missing.length) throw new Error(`Missing building types: ${missing.join(", ")}`);
  for (const building of buildings) {
    const def = EM2C_BUILDINGS[building.type];
    const cells = rotateFootprint(def.footprintHexes, building.rotation);
    if (!cells.every((c) => grid.get(hexKey(building.q + c.q, building.r + c.r))?.buildingId === building.id)) {
      throw new Error(`Footprint overlap: ${building.type}`);
    }
  }

  // Warm inventories, not invincibility: normal consumption, production, shortages
  // and emergencies continue after loading. Caps are recalculated by the simulator.
  const resources = {};
  for (const [key, value] of Object.entries(state.resources || {})) {
    const max = Math.max(1, Number(value?.max || 500));
    resources[key] = { ...value, max, stored: max * 0.45 };
  }
  // Provide adequate starting utility reserves and production feedstock.
  for (const key of ["power", "water", "oxygen", "food", "raw_water",
    "potable_water", "raw_ore", "metal_ore", "refined_metal", "machine_parts",
    "polymers", "hydrogen", "methane", "co2", "nitrogen", "fertilizer",
    "nutrients", "reactor_fuel", "credits"]) {
    const old = resources[key] || { max: 500 };
    resources[key] = { ...old, stored: Number(old.max) * 0.7 };
  }

  // 48 real colonists, housing supplied by the scenario's modular habitats.
  const colonists = Array.from({ length: 48 }, (_, i) => {
    const pos = axialToWorld(-3 + (i % 8), 1 + Math.floor(i / 8));
    return {
      id: `showcase-colonist-${i + 1}`, name: `Crew ${String(i + 1).padStart(2, "0")}`,
      profession: ["Engineer", "Botanist", "Medic", "Geologist"][i % 4],
      trait: "Resilient", health: 1, morale: 0.9, hunger: 0.1,
      x: pos.x, z: pos.z, tx: pos.x, tz: pos.z, moveTimer: 0,
    };
  });
  const housing = buildings.reduce((n, b) => n + Number(EM2C_BUILDINGS[b.type]?.residentCapacity || 0), 0);
  if (housing < colonists.length) throw new Error(`Housing shortage: ${housing} for ${colonists.length} colonists`);

  return {
    grid, deposits, buildings, colonists, selectedRecipes,
    nextBuildingNumber: buildings.length + 1, _autoIdleIds: [], _autoControlLastAt: 0,
    resources, research: { completed: TECHS.map((t) => t.id), active: null },
    colonyTier: 4, sol: 3, time: 0.28, gameSpeed: 1, isPaused: true,
    placementType: null, hoverHex: null, placementRotation: 0,
    selectedId: null, moveBuildingId: null, moveCandidate: null,
    defeat: false, defeatReason: null, victory: false, gridVisible: false,
    _colonyAlert: "NORMAL", _controllerShedIds: [], _restartHold: [],
    _colonyControllerState: null, _lastRestartAt: 0,
    activeEvent: null, eventTimeLeft: 0,
    storm: { active: false, timeLeft: 0, warning: false, next: 5 },
    objectives: (state.objectives || []).map((o) => ({
      ...o, done: Boolean(o.type && types.has(o.type)),
    })),
    notifications: [],
    showcaseColony: true,
  };
}

export function loadShowcaseColony() {
  const before = useGame.getState();
  const scenario = planShowcaseColony(before);
  // Keep a separate copy of existing local saves. Do not overwrite this backup
  // on repeated scenario loads. It can be restored independently if necessary.
  try {
    before.save?.("manual");
    for (const key of SAVE_KEYS) {
      const backupKey = SHOWCASE_BACKUP_PREFIX + key;
      if (localStorage.getItem(backupKey) == null) {
        const value = localStorage.getItem(key);
        if (value != null) localStorage.setItem(backupKey, value);
      }
    }
  } catch (e) {
    throw new Error("Could not back up the existing colony: " + String(e.message || e));
  }
  useGame.setState(scenario);
  useGame.getState().save?.("manual");
  useGame.getState().pushNotification?.({
    level: "info",
    text: `Showcase colony loaded: ${scenario.buildings.length} modules, all ${EM2C_BUILDING_LIST.length} building types. Paused for inspection.`,
  });
  return { total: scenario.buildings.length, unique: EM2C_BUILDING_LIST.length };
}

export function hasShowcaseBackup() {
  try { return Boolean(localStorage.getItem(SHOWCASE_BACKUP_PREFIX + "mars_em2c_save_v1")); }
  catch (_) { return false; }
}

export function restoreBeforeShowcase() {
  if (!hasShowcaseBackup()) throw new Error("No previous-colony backup is available in this browser");
  // First verify every present JSON backup before touching active save slots.
  const backedUp = SAVE_KEYS.map((key) => {
    const content = localStorage.getItem(SHOWCASE_BACKUP_PREFIX + key);
    if (content != null) JSON.parse(content);
    return [key, content];
  });
  for (const [key, content] of backedUp) {
    if (content == null) localStorage.removeItem(key);
    else localStorage.setItem(key, content);
  }
  useGame.getState().load?.();
  useGame.setState({ showcaseColony: false });
  return true;
}
